import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ColorPickerModule } from '@iplab/ngx-color-picker';
import { TemplateService } from '../../../car-wash-website/services/template.service';
import { SectionAccordionComponent } from '../../../shared/components/section-accordion';
import {
  FooterLink,
  SocialLink,
} from '@way/website-themes/themes/minimal_website/shared/footer/footer.component';
import { resolveFooterQuickLinks } from '../../../shared/utils/footer-quick-links.util';

export interface PageInfo {
  name: string;
  pageType?: string;
  pageId?: string;
  visible?: boolean;
  active?: boolean;
}

export interface FooterData {
  logo?: {
    image?: string;
    text?: string;
  };
  quickLinks?: FooterLink[];
  quickLinksTitle?: string;
  socialLinks?: SocialLink[];
  socialLinksTitle?: string;
  copyright?: string;
  backgroundColor?: string;
}

@Component({
  selector: 'app-footer-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, ColorPickerModule, SectionAccordionComponent],
  templateUrl: './footer-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./footer-panel.component.scss'],
})
export class FooterPanelComponent implements OnInit, OnChanges {
  @Input() data: FooterData = {
    logo: {
      text: 'way',
    },
    quickLinks: [],
    socialLinks: [
      { platform: 'Instagram', url: '#', icon: 'instagram' },
      { platform: 'Facebook', url: '#', icon: 'facebook' },
      { platform: 'YouTube', url: '#', icon: 'youtube' },
      { platform: 'LinkedIn', url: '#', icon: 'linkedin' },
      { platform: 'X', url: '#', icon: 'x' },
    ],
    copyright: `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`,
    backgroundColor: '#000920',
  };

  @Input() pages: PageInfo[] = [];

  @Output() dataChange = new EventEmitter<FooterData>();
  @Output() closePanel = new EventEmitter<void>();

  activeTab: 'content' | 'layout' = 'content';
  expandedSection: string | null = 'media'; // Single expanded section

  // File upload state
  selectedFile: File | null = null;
  logoPreview: string | null = null;
  isUploadingLogo = false;
  uploadError: string | null = null;
  showReplaceButton = false;

  // Page title validation
  pageTitleErrors: Record<number, string | null> = {};
  readonly MAX_PAGE_TITLE_LENGTH = 20;

  // Social link validation
  socialLinkErrors: Record<number, string | null> = {};
  previousPlatforms: Record<number, string> = {};

  // Color picker state (same as hero panel)
  isColorPickerOpen = false;
  currentPickerColor: string | any = '#000000';
  customColors: string[] = [];

  private static readonly PREDEFINED_COLORS = ['#5E19A1', '#FF9736', '#079271', '#1B5BFF'];

  constructor(private templateService: TemplateService) {}

  /**
   * Initialize the footer panel state from provided data and set up UI defaults and previews.
   * @example
   * gOnInit()
   * undefined
   * @returns {void} Initializes component state (quick links, expanded sections, logo preview, social links, previous platforms, background color, color picker) and does not return a value.
   **/
  ngOnInit(): void {
    this.initializeQuickLinks();
    this.initializeExpandedSections();

    // Initialize logo preview if logo image exists
    if (this.data.logo?.image) {
      this.logoPreview = this.data.logo.image;
      this.showReplaceButton = true;
    }

    // Initialize social links if not provided
    if (!this.data.socialLinks || this.data.socialLinks.length === 0) {
      this.data.socialLinks = [
        { platform: 'Instagram', url: '#', icon: 'instagram' },
        { platform: 'Facebook', url: '#', icon: 'facebook' },
        { platform: 'YouTube', url: '#', icon: 'youtube' },
        { platform: 'LinkedIn', url: '#', icon: 'linkedin' },
        { platform: 'X', url: '#', icon: 'x' },
      ];
    }

    // Initialize previous platforms tracking
    this.initializePreviousPlatforms();

    // Ensure backgroundColor is set from API data (from footer.theme.background_color)
    if (!this.data.backgroundColor) {
      this.data.backgroundColor = '#000920'; // Default fallback
    }

    // Initialize color picker with current background color from API
    this.currentPickerColor = this.data.backgroundColor || '#000000';

    // Load custom color on initial load
    this.loadCustomColorFromData();
  }

  private initializePreviousPlatforms(): void {
    if (this.data.socialLinks) {
      this.data.socialLinks.forEach((social, index) => {
        this.previousPlatforms[index] = social.platform || '';
      });
    }
  }

  /**
   * Responds to Angular input changes (pages, data, backgroundColor) to update quick links, logo preview, social platforms, and color picker.
   * @example
   * gOnChanges({ pages: [{ title: 'Home' }], data: { logo: { image: 'https://...' }, socialLinks: [], backgroundColor: '#ffffff' } })
   * undefined
   * @param {{SimpleChanges}} {{changes}} - Angular SimpleChanges object containing previous and current values for component inputs.
   * @returns {{void}} Does not return a value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    // Re-initialize quick links when pages input changes
    if (changes['pages'] && this.pages && this.pages.length > 0) {
      this.initializeQuickLinks();
      this.initializeExpandedSections();
    }

    // Update logo preview when data changes
    if (changes['data'] && this.data?.logo?.image) {
      this.logoPreview = this.data.logo.image;
      this.showReplaceButton = true;
    }

    // Re-initialize previous platforms when social links change
    if (changes['data'] && this.data?.socialLinks) {
      this.initializePreviousPlatforms();
    }

    // Update color picker when backgroundColor changes from API (from footer.theme.background_color)
    if (changes['data'] && this.data?.backgroundColor) {
      // Update current picker color to match API color
      this.currentPickerColor = this.data.backgroundColor;
      // Load custom color if backgroundColor is not in predefined colors
      this.loadCustomColorFromData();
    }
  }

  /**
   * Generate quick links for the footer from active pages or set a default Home link.
   * @example
   * initializeQuickLinks()
   * undefined
   * @param {void} - No parameters.
   * @returns {void} No return value.
   **/
  private initializeQuickLinks(): void {
    if (this.pages && this.pages.length > 0) {
      this.data.quickLinks = resolveFooterQuickLinks(this.data.quickLinks, this.pages);
      return;
    }
    if (!this.data.quickLinks || this.data.quickLinks.length === 0) {
      this.data.quickLinks = [{ label: 'Home', link: '#home' }];
    }
  }

  private initializeExpandedSections(): void {
    // Initialize with media section expanded by default
    this.expandedSection = 'media';
  }

  onClose(): void {
    this.closePanel.emit();
  }

  switchTab(tab: 'content' | 'layout'): void {
    this.activeTab = tab;
  }

  /**
   * Toggle the expanded/collapsed state of a footer panel section.
   * @example
   * oggleSection('contact')
   * undefined
   * @param {{string}} {{section}} - Section identifier to toggle open or closed.
   * @returns {{void}} Nothing is returned; the component's expandedSection state is updated.
   **/
  toggleSection(section: string): void {
    // Same logic as header section: if clicking an expanded section, collapse it
    // Otherwise, expand this one
    const wasExpanded = this.expandedSection === section;

    if (wasExpanded) {
      // If clicking an expanded section, just collapse it
      this.expandedSection = null;
    } else {
      // If clicking a collapsed section, expand this one
      this.expandedSection = section;
    }
  }

  isSectionExpanded(section: string): boolean {
    return this.expandedSection === section;
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.handleFileUpload(file);
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileUpload(files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Validate and upload a logo file (images or mp4), update preview and component state.
   * @example
   * handleFileUpload(new File(['...'], 'logo.png', { type: 'image/png' }))
   * undefined
   * @param {File} file - The file object to validate and upload (.png, .jpg, .jpeg, .gif, .svg, .mp4; max 5MB).
   * @returns {void} Does not return a value; updates component state and handles upload side effects.
   **/
  private handleFileUpload(file: File): void {
    // Validate file type - allow .png, .jpg, .jpeg, .mp4 (based on Figma)
    const validTypes = [
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/gif',
      'image/svg+xml',
      'video/mp4',
    ];
    const validExtensions = ['.png', '.jpg', '.jpeg', '.mp4'];
    const fileName = file.name.toLowerCase();
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

    if (!validTypes.includes(file.type) && !hasValidExtension) {
      this.uploadError = 'Invalid file type. Accepted formats - .png, .jpg, .jpeg, .mp4';
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      this.uploadError = 'Max file size - 5MB';
      return;
    }

    this.uploadError = null;
    this.selectedFile = file;
    this.isUploadingLogo = true;

    // Upload file to server
    this.templateService.uploadWebsiteFile(file).subscribe({
      next: uploadResponse => {
        console.log('Logo uploaded successfully:', uploadResponse);

        // Get logo URL from response
        const logoUrl =
          uploadResponse?.data?.url ||
          uploadResponse?.data?.file ||
          uploadResponse?.data?.file_url ||
          uploadResponse?.url ||
          uploadResponse?.file ||
          uploadResponse?.file_url ||
          (uploadResponse?.data && typeof uploadResponse.data === 'string'
            ? uploadResponse.data
            : null);

        if (logoUrl) {
          console.log('Logo URL extracted:', logoUrl);
          this.logoPreview = logoUrl;
          if (this.data.logo) {
            this.data.logo.image = logoUrl;
          } else {
            this.data.logo = {
              image: logoUrl,
              text: 'way',
            };
          }
          this.showReplaceButton = true;
          this.isUploadingLogo = false;
          this.emitChange();
        } else {
          console.error('Logo URL not found in upload response');
          this.uploadError = 'Failed to upload logo. Please try again.';
          this.isUploadingLogo = false;
        }
      },
      error: error => {
        console.error('Error uploading logo:', error);
        this.uploadError = 'Failed to upload logo. Please try again.';
        this.isUploadingLogo = false;
      },
    });
  }

  onReplaceLogo(): void {
    // Trigger file input click
    const fileInput = document.getElementById('footer-logo-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  onQuickLinksTitleChange(): void {
    this.emitChange();
  }

  /**
   * Update a quick link's label at the given index, validating and truncating the title if it exceeds the maximum, setting a transient error message, and emitting a change.
   * @example
   * nQuickLinkChange(2, 'Homepage')
   * undefined
   * @param {number} index - Index of the quick link to update.
   * @param {string} [newValue] - New label value; if omitted the link is left unchanged.
   * @returns {void} Void; updates internal state and emits a change event.
   **/
  onQuickLinkChange(index: number, newValue?: string): void {
    const link = this.data.quickLinks?.[index];
    if (!link) return;

    // If newValue is provided, validate it
    if (newValue !== undefined) {
      // Validate page title length
      if (newValue && newValue.length > this.MAX_PAGE_TITLE_LENGTH) {
        // Truncate to max length
        link.label = newValue.substring(0, this.MAX_PAGE_TITLE_LENGTH);
        // Show error message
        this.pageTitleErrors[index] =
          `Page title cannot exceed ${this.MAX_PAGE_TITLE_LENGTH} characters`;
        // Clear error after 3 seconds
        setTimeout(() => {
          this.pageTitleErrors[index] = null;
        }, 3000);
      } else {
        // Update label and clear error
        link.label = newValue || '';
        this.pageTitleErrors[index] = null;
      }
    }

    this.emitChange();
  }

  /**
   * Enforces the maximum page title length for a given input, truncates excess characters, and sets/clears error messages.
   * @example
   * nPageTitleInput(new Event('input'), 0)
   * undefined
   * @param {Event} event - The input event from the page title input element.
   * @param {number} index - The index of the page title field being edited.
   * @returns {void} Updates the input value and component's pageTitleErrors; does not return a value.
   **/
  onPageTitleInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const newValue = input.value;

    // Prevent typing beyond the limit
    if (newValue.length > this.MAX_PAGE_TITLE_LENGTH) {
      input.value = newValue.substring(0, this.MAX_PAGE_TITLE_LENGTH);
      this.pageTitleErrors[index] =
        `Page title cannot exceed ${this.MAX_PAGE_TITLE_LENGTH} characters`;
      // Clear error after 3 seconds
      setTimeout(() => {
        this.pageTitleErrors[index] = null;
      }, 3000);
    } else {
      // Clear error if within limit
      this.pageTitleErrors[index] = null;
    }
  }

  onSocialLinksTitleChange(): void {
    this.emitChange();
  }

  /**
   * Handle change to a social link platform at a given index, preventing duplicates and emitting updates.
   * @example
   * nSocialLinkChange(0, 'Twitter')
   * undefined
   * @param {{number}} {{index}} - Index of the social link being changed.
   * @param {{string}} {{newPlatform}} - Optional new platform value from user input or event; if omitted the current platform is used.
   * @returns {{void}} No return value; updates component state, shows/clears validation errors, and emits change events when appropriate.
   **/
  onSocialLinkChange(index: number, newPlatform?: string): void {
    const social = this.data.socialLinks?.[index];
    if (!social) return;

    // Store previous platform if not already stored
    if (!this.previousPlatforms[index]) {
      this.previousPlatforms[index] = social.platform || '';
    }

    // Get the platform to check (newPlatform from event or current platform)
    const platformToCheck = newPlatform !== undefined ? newPlatform : social.platform;

    // Check for duplicate platform
    const isDuplicate = this.isPlatformUsed(platformToCheck, index);

    if (isDuplicate && platformToCheck) {
      // Revert to previous platform
      const previousPlatform = this.previousPlatforms[index] || '';
      social.platform = previousPlatform;

      // Show error message
      this.socialLinkErrors[index] =
        `${platformToCheck} is already added. Please choose a different platform.`;
      // Clear error after 3 seconds
      setTimeout(() => {
        this.socialLinkErrors[index] = null;
      }, 3000);

      // Don't emit change since we reverted
      return;
    } else {
      // Update previous platform for next change
      if (platformToCheck) {
        this.previousPlatforms[index] = platformToCheck;
      }
      // Clear error if no duplicate
      this.socialLinkErrors[index] = null;
    }

    this.emitChange();
  }

  /**
   * Check if a platform is already used by another social link
   */
  isPlatformUsed(platform: string, currentIndex: number): boolean {
    if (!this.data.socialLinks || !platform) return false;

    return this.data.socialLinks.some(
      (social, index) =>
        index !== currentIndex &&
        social.platform &&
        social.platform.toLowerCase() === platform.toLowerCase()
    );
  }

  /**
   * Check if a platform option should be disabled (already used by another link)
   */
  isPlatformDisabled(platform: string, currentIndex: number): boolean {
    return this.isPlatformUsed(platform, currentIndex);
  }

  /**
   * Add a new default social link object to data.socialLinks, initialize its previous platform tracking, and emit a change event.
   * @example
   * ddSocialLink()
   * // undefined
   * @returns {void} No return value.
   **/
  addSocialLink(): void {
    if (!this.data.socialLinks) {
      this.data.socialLinks = [];
    }
    const newIndex = this.data.socialLinks.length;
    this.data.socialLinks.push({
      platform: 'New Platform',
      url: '#',
      icon: 'link',
    });
    // Initialize previous platform for new link
    this.previousPlatforms[newIndex] = 'New Platform';
    this.emitChange();
  }

  /**
   * Removes a social link at the given index, reindexes previous platform tracking, clears related errors, and emits a change.
   * @example
   * emoveSocialLink(1)
   * undefined
   * @param {{number}} {{index}} - Zero-based index of the social link to remove.
   * @returns {{void}} Void; no value is returned.
   **/
  removeSocialLink(index: number): void {
    if (this.data.socialLinks && this.data.socialLinks.length > index) {
      this.data.socialLinks.splice(index, 1);
      // Clean up previous platform tracking
      delete this.previousPlatforms[index];
      // Re-index previous platforms
      const newPreviousPlatforms: Record<number, string> = {};
      this.data.socialLinks.forEach((social, newIndex) => {
        if (this.previousPlatforms[newIndex + 1]) {
          newPreviousPlatforms[newIndex] = this.previousPlatforms[newIndex + 1];
        } else {
          newPreviousPlatforms[newIndex] = social.platform || '';
        }
      });
      this.previousPlatforms = newPreviousPlatforms;
      // Clear error for removed index
      delete this.socialLinkErrors[index];
      this.emitChange();
    }
  }

  onCopyrightChange(): void {
    this.emitChange();
  }

  onBackgroundColorChange(color: string): void {
    this.data.backgroundColor = color;
    this.emitChange();
  }

  onBackgroundChange(): void {
    this.emitChange();
  }

  // Load custom color from data if it's not a predefined color
  /**
   * Load a background color from the component's data and set it as a single custom color if it is not one of the predefined colors.
   * @example
   * // If this.data.backgroundColor is '#abc123' and not in PREDEFINED_COLORS:
   * component.loadCustomColorFromData()
   * // component.customColors -> ['#abc123']
   * @returns {void} Updates this.customColors based on this.data.backgroundColor.
   */
  private loadCustomColorFromData(): void {
    if (!this.data?.backgroundColor) {
      return;
    }

    const bgColor = this.data.backgroundColor;

    // Check if the color is not in predefined colors
    if (!FooterPanelComponent.PREDEFINED_COLORS.includes(bgColor)) {
      // Add to custom colors (maximum 1 custom color)
      this.customColors = [bgColor];
    } else {
      // If it's a predefined color, clear custom colors
      this.customColors = [];
    }
  }

  onAddColor(): void {
    // Open color picker modal
    this.openColorPicker();
  }

  // Open color picker for custom color
  openColorPicker(color?: string): void {
    this.currentPickerColor = color || this.data.backgroundColor || '#000920';
    this.isColorPickerOpen = true;
  }

  // Close color picker and add the selected color to custom colors list
  /**
   * Close the color picker, extract and normalize the selected hex color (ensuring a leading '#'), apply it to the background, and add it as the single custom color if present.
   * @example
   * loseColorPicker()
   * undefined
   * @param {void} none - No arguments.
   * @returns {void} Nothing.
   **/
  closeColorPicker(): void {
    if (this.currentPickerColor) {
      // Extract hex color from color object (chrome-picker returns an object with hex property)
      let hexColor: string;
      if (typeof this.currentPickerColor === 'string') {
        hexColor = this.currentPickerColor;
      } else if (
        this.currentPickerColor &&
        typeof this.currentPickerColor === 'object' &&
        'hex' in this.currentPickerColor
      ) {
        hexColor = (this.currentPickerColor as any).hex;
      } else {
        hexColor = String(this.currentPickerColor);
      }

      // Ensure hex color starts with #
      if (!hexColor.startsWith('#')) {
        hexColor = '#' + hexColor;
      }

      // Apply the color immediately
      this.onBackgroundColorChange(hexColor);

      // Add to custom colors (maximum 1 custom color - replace if exists)
      this.addCustomColor(hexColor);
    }
    this.isColorPickerOpen = false;
  }

  // Handle color change from color picker (update preview only, don't add to list yet)
  /**
   * Normalize the selected color to a hex string, update the component state, and apply it for preview.
   * @example
   * nColorPickerChange()
   * undefined
   * @param {{void}} {{}} - No parameters.
   * @returns {{void}} Return nothing.
   **/
  onColorPickerChange(): void {
    if (this.currentPickerColor) {
      // Extract hex color from color object (chrome-picker returns an object with hex property)
      let hexColor: string;
      if (typeof this.currentPickerColor === 'string') {
        hexColor = this.currentPickerColor;
      } else if (
        this.currentPickerColor &&
        typeof this.currentPickerColor === 'object' &&
        'hex' in this.currentPickerColor
      ) {
        hexColor = (this.currentPickerColor as any).hex;
      } else {
        hexColor = String(this.currentPickerColor);
      }

      // Ensure hex color starts with #
      if (!hexColor.startsWith('#')) {
        hexColor = '#' + hexColor;
      }

      // Update currentPickerColor to the hex string for consistency
      this.currentPickerColor = hexColor;

      // Apply the color immediately for preview (but don't add to custom colors list yet)
      this.onBackgroundColorChange(hexColor);
    }
  }

  private addCustomColor(color: string): void {
    // Maximum 1 custom color - replace existing if any
    if (!FooterPanelComponent.PREDEFINED_COLORS.includes(color)) {
      // Clear existing custom colors and add the new one
      this.customColors = [color];
    }
  }

  private emitChange(): void {
    this.dataChange.emit(this.data);
  }

  getQuickLinks(): FooterLink[] {
    return this.data.quickLinks || [];
  }

  getSocialLinks(): SocialLink[] {
    return this.data.socialLinks || [];
  }

  getSocialIconOptions(): string[] {
    return ['instagram', 'facebook', 'youtube', 'linkedin', 'x', 'twitter', 'pinterest', 'tiktok'];
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
